// src/hooks/useOverflow.js
import { useEffect, useState } from 'react';

/**
 * How many of a row's items fit, and which ones have to move into its overflow
 * menu.
 *
 * Written as a pure function over declared widths rather than by measuring each
 * button: the decision has to be made BEFORE the row is painted, or the bar
 * visibly reflows on every resize. Widths are declared by the caller and tested
 * directly — see FileBrowserToolbar.
 *
 * An unmeasured row (`available` 0, which is what jsdom and the first paint
 * report) keeps everything visible: showing a collapsed bar for one frame and
 * then expanding it looks like a glitch, while the reverse does not.
 */
export function fitToWidth({ available, items, overflowWidth = 40, gap = 8 }) {
    const widthOf = (list) => list.reduce((sum, item, i) => sum + item.width + (i ? gap : 0), 0);

    if (!available || widthOf(items) <= available) return { visible: items, overflow: [] };

    // Anything overflowing means the trigger has to fit too.
    const budget = available - overflowWidth - gap;
    const visible = [];
    let used = 0;
    for (const item of items) {
        const next = used + item.width + (visible.length ? gap : 0);
        if (next > budget) break;
        visible.push(item);
        used = next;
    }
    return { visible, overflow: items.slice(visible.length) };
}

/**
 * The element's inner width, tracked as it changes.
 *
 * Returns 0 where ResizeObserver isn't available (jsdom), which `fitToWidth`
 * reads as "unmeasured — show everything".
 */
export function useElementWidth(ref) {
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        if (typeof ResizeObserver === 'undefined') {
            setWidth(el.getBoundingClientRect?.().width ?? 0);
            return undefined;
        }
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            setWidth(entry?.contentRect?.width ?? 0);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);

    return width;
}
